import React, { useState } from 'react';

import { List, MenuItem, Typography, useTheme } from '@mui/material';
import Grid from '@mui/material/Grid';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, reduxForm } from 'redux-form/immutable';

import Button from 'components/Button';
import CleanableTextField from 'components/CleanableTextField';
import DeleteAccount from 'components/DeleteAccount';
import DetailListItem from 'components/DetailListItem';
import SelectFieldOne from 'components/reduxFormComponentsV3/SelectFieldOne';
import Control from 'components/SvgComponents/icons/Control';
import Edit from 'components/SvgComponents/icons/Edit';
import Tile from 'components/Tile/Tile';
import VerifyEmailButton from 'containers/AccountSettings/components/VerifyEmailButton';
import { useFeatureFlags } from 'contexts/FeatureFlagsProvider';
import useAuthSession from 'hooks/useAuthSession';
import globalMessages from 'translations/messages/global-messages';
import { getTempTemplateLanguagesDisplay, tempTemplateLanguages } from 'utils/constants';
import { normalizePhone } from 'utils/validators';

export const styles = theme => ({
  contentPassword: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  checkIcon: {
    color: theme.palette.secondary.main,
  },
  emailTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
});

export function UserForm(props) {
  const {
    classes,
    userInfo,
    handleSubmit,
    intl,
    language,
    userAvatarComp,
    onDeleteAccount,
  } = props;
  const featureFlags = useFeatureFlags();
  const { isIDPUser } = useAuthSession();
  const [openDeleteModal, setOpenDeleteModal] = useState();
  const theme = useTheme();
  const isEmailVerified = userInfo?.email_validated || userInfo?.is_email_verified;

  const handleSetOpenDeleteModal = () => {
    setOpenDeleteModal(!openDeleteModal);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={classes.wrapper}>
        <Grid container spacing={10}>
          <Grid item xs="auto">
            {userAvatarComp}
          </Grid>
          <Grid item xs>
            <List sx={{ p: 0 }}>
              <DetailListItem
                label={intl.formatMessage(globalMessages.first_name)}
                detail={
                  <Field
                    required
                    name="first_name"
                    component={CleanableTextField}
                    placeholder={intl.formatMessage(globalMessages.first_name)}
                  />
                }
              />
              <DetailListItem
                label={intl.formatMessage(globalMessages.last_name)}
                detail={
                  <Field
                    required
                    name="last_name"
                    component={CleanableTextField}
                    placeholder={intl.formatMessage(globalMessages.last_name)}
                  />
                }
              />
            </List>
          </Grid>
        </Grid>
        <List sx={{ p: 0 }}>
          <DetailListItem
            label={intl.formatMessage(globalMessages.phone_number)}
            detail={
              <Field
                normalize={normalizePhone}
                name="phoneNumber.value"
                component={CleanableTextField}
                clearable
                clearableOnlyOnFocus
                placeholder={intl.formatMessage(globalMessages.phone_number)}
              />
            }
          />
          <DetailListItem
            edit
            label={
              <Typography variant="body" className={classes.emailTitle}>
                {intl.formatMessage(globalMessages.email)}
                <Tile
                  label={intl.formatMessage(
                    globalMessages[isEmailVerified ? 'verified' : 'to_verify'],
                  )}
                  color={isEmailVerified ? 'success' : 'warning'}
                />
              </Typography>
            }
            detail={
              <div className={classes.contentPassword}>
                <Field
                  name="email"
                  component={CleanableTextField}
                  disabled
                  placeholder={intl.formatMessage(globalMessages.phone_number)}
                />
                {!isEmailVerified && <VerifyEmailButton />}
                {!isIDPUser ? (
                  <Button onClick={() => props.onResetPassword(userInfo?.email)} variant="outlined">
                    <Edit width={16} height={16} color={theme.palette.secondary.main} />
                    {intl.formatMessage(globalMessages.reset_password)}
                  </Button>
                ) : null}
              </div>
            }
          />
          <DetailListItem
            label={intl.formatMessage(globalMessages.language)}
            detail={
              <Field
                name="locale"
                placeholder={intl.formatMessage(globalMessages.select_language)}
                component={SelectFieldOne}
                renderValue={() => getTempTemplateLanguagesDisplay(language)}
              >
                {tempTemplateLanguages.map(option => (
                  <MenuItem
                    key={option.code}
                    selected={language === option.code}
                    value={option.code}
                    data-guid={option.code}
                  >
                    <Typography variant="subtitle" className={classes.item}>
                      {getTempTemplateLanguagesDisplay(option.code)}
                      {language === option.code && (
                        <Control className={classes.checkIcon} width={22} height={22} />
                      )}
                    </Typography>
                  </MenuItem>
                ))}
              </Field>
            }
          />
        </List>
      </div>
      <Typography variant="h6" sx={{ mb: 4 }}>
        {intl.formatMessage(globalMessages.account_settings_danger_zone)}
      </Typography>
      {featureFlags?.delete_account && (
        <DeleteAccount
          userEmail={userInfo.email}
          openModal={openDeleteModal}
          setOpenModal={handleSetOpenDeleteModal}
          onDeleteAccount={onDeleteAccount}
        />
      )}
    </form>
  );
}

UserForm.propTypes = {
  intl: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  handleSubmit: PropTypes.func,
  onResetPassword: PropTypes.func,
  userInfo: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  language: PropTypes.string,
  userAvatarComp: PropTypes.node,
  onDeleteAccount: PropTypes.func,
};
const mapStateToFormProps = (state, ownProps) => {
  const formString = JSON.stringify(state.form.get(ownProps.form), null, 2);
  const formValues = formString ? JSON.parse(formString) : '';
  const language = formValues?.values?.locale;
  const mission = formValues?.values?.mission;
  return { language, mission };
};
const withConnect = connect(
  mapStateToFormProps,
  null,
);
export default compose(
  injectIntl,
  withConnect,
  reduxForm({
    destroyOnUnmount: true,
    enableReinitialize: true,
  }),
  withStyles(styles),
)(UserForm);
